import React from "react";
import { Box, Typography, Card, CardContent, List, ListItem } from "@mui/material";
import { getWithToken } from "../fetchWithToken";
import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, AccountBalanceWallet } from "@mui/icons-material";

export const MonthlyOverview = ({ year, month }) => {
    const [basicData, setBasicData] = useState(null);
    const [topEntries, setTopEntries] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getWithToken('/api/statistic/monthly_basic/', { year, month }),
            getWithToken('/api/statistic/monthly_top_entries/', { year, month })
        ])
            .then(([basic, top]) => {
                setBasicData(basic);
                setTopEntries(top);
            })
            .catch(err => {
                console.error('获取月度数据失败:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [year, month]);

    if (loading || !basicData || !topEntries) {
        return (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {[1, 2, 3].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: 1,
                            minWidth: 180,
                            height: 75,
                            bgcolor: 'grey.100',
                            borderRadius: 2
                        }}
                    />
                ))}
            </Box>
        );
    }

    const overviewItems = [
        {
            label: '本月收入',
            value: `¥${basicData.income.total.toLocaleString()}`,
            icon: <TrendingUp />,
            color: '#ef4444',
            bgColor: '#fef2f2'
        },
        {
            label: '本月支出',
            value: `¥${basicData.expense.total.toLocaleString()}`,
            icon: <TrendingDown />,
            color: '#10b981',
            bgColor: '#ecfdf5'
        },
        {
            label: '本月结余',
            value: `¥${basicData.balance.toLocaleString()}`,
            icon: <AccountBalanceWallet />,
            color: basicData.balance >= 0 ? '#3b82f6' : '#f59e0b',
            bgColor: basicData.balance >= 0 ? '#eff6ff' : '#fffbeb'
        }
    ];

    return (
        <Box sx={{ mb: 2 }}>
            {/* 总览卡片 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {overviewItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: 1,
                            minWidth: 180,
                            bgcolor: item.bgColor,
                            borderRadius: 2.5,
                            p: 1.75,
                            transition: 'all 0.3s ease',
                            border: '1px solid transparent',
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                borderColor: item.color
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box
                                sx={{
                                    p: 0.75,
                                    borderRadius: 1.5,
                                    bgcolor: item.color,
                                    color: 'white',
                                    display: 'flex'
                                }}
                            >
                                {item.icon}
                            </Box>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: item.color, fontSize: '1.5rem' }}>
                            {item.value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.8rem' }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* 最大5笔 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                {/* 最大5笔支出 */}
                <Card sx={{ flex: 1, borderRadius: 2.5, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'visible' }}>
                    <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{
                                p: 0.5,
                                borderRadius: 1.5,
                                bgcolor: '#ecfdf5',
                                color: '#10b981',
                                display: 'flex'
                            }}>
                                <TrendingDown sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>
                                最大5笔支出
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0 }}>
                            {topEntries.top_expense.length > 0 ? topEntries.top_expense.map((entry, index) => (
                                <React.Fragment key={entry.id}>
                                    <ListItem
                                        sx={{
                                            px: 0,
                                            py: 0.75,
                                            borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                bgcolor: index < 3 ? '#ecfdf5' : 'grey.100',
                                                color: index < 3 ? '#10b981' : 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                mr: 1,
                                                flexShrink: 0,
                                                mt: 0.2
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    color: '#374151',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                                title={entry.title}
                                            >
                                                {entry.title}
                                            </Typography>
                                            {entry.content && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#9ca3af',
                                                        fontSize: 10,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block'
                                                    }}
                                                    title={entry.content}
                                                >
                                                    {entry.content.length > 20 ? entry.content.substring(0, 20) + '...' : entry.content}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 11 }}>
                                                {entry.category_name} · {entry.date}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981', fontSize: 13, flexShrink: 0, mt: 0.2 }}>
                                            -¥{entry.amount.toLocaleString()}
                                        </Typography>
                                    </ListItem>
                                </React.Fragment>
                            )) : (
                                <ListItem sx={{ px: 0 }}>
                                    <Typography variant="body2" color="text.secondary" fontSize={13}>暂无数据</Typography>
                                </ListItem>
                            )}
                        </List>
                    </CardContent>
                </Card>

                {/* 最大5笔收入 */}
                <Card sx={{ flex: 1, borderRadius: 2.5, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'visible' }}>
                    <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{
                                p: 0.5,
                                borderRadius: 1.5,
                                bgcolor: '#fef2f2',
                                color: '#ef4444',
                                display: 'flex'
                            }}>
                                <TrendingUp sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>
                                最大5笔收入
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0 }}>
                            {topEntries.top_income.length > 0 ? topEntries.top_income.map((entry, index) => (
                                <React.Fragment key={entry.id}>
                                    <ListItem
                                        sx={{
                                            px: 0,
                                            py: 0.75,
                                            borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                bgcolor: index < 3 ? '#fef2f2' : 'grey.100',
                                                color: index < 3 ? '#ef4444' : 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                mr: 1,
                                                flexShrink: 0,
                                                mt: 0.2
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    color: '#374151',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                                title={entry.title}
                                            >
                                                {entry.title}
                                            </Typography>
                                            {entry.content && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#9ca3af',
                                                        fontSize: 10,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block'
                                                    }}
                                                    title={entry.content}
                                                >
                                                    {entry.content.length > 20 ? entry.content.substring(0, 20) + '...' : entry.content}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 11 }}>
                                                {entry.category_name} · {entry.date}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444', fontSize: 13, flexShrink: 0, mt: 0.2 }}>
                                            +¥{entry.amount.toLocaleString()}
                                        </Typography>
                                    </ListItem>
                                </React.Fragment>
                            )) : (
                                <ListItem sx={{ px: 0 }}>
                                    <Typography variant="body2" color="text.secondary" fontSize={13}>暂无数据</Typography>
                                </ListItem>
                            )}
                        </List>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
